import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';
import {
  ICreateDataMaskingDto,
  IUpdateDataMaskingDto,
  IGetDataMaskingParams,
  IDataMaskingListResponse,
  IDataMaskingResponse,
} from '../../pages/TravelRequest/constants/secureMasking.interface';

const masterApi = createApi({
  reducerPath: 'master',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['CompanyGroupModuleMapping'],
  endpoints: builder => ({
    getMasterChangeLogs: builder.query<
      BackendResponse<{ list: any[]; count: number }>,
      {
        type: 'MATERIAL' | 'CUSTOMER' | 'VENDOR' | string;
        recordId: number;
        page?: number;
        pageSize?: number;
        search?: string;
        sortOrder?: 'asc' | 'desc';
      }
    >({
      query: params => ({
        url: `/master/change-logs${parseQueryParams(params)}`,
        method: 'GET',
      }),
    }),
    getSalesOrganisation: builder.query<BackendResponse<any>, any>({
      query: params => {
        return {
          url: `/master/sales-organisations/all${parseQueryParams(params)}`,
        };
      },
    }),
    getDistributionChannel: builder.query<BackendResponse<any>, any>({
      query: params => {
        return {
          url: `/master/distribution-channels/all${parseQueryParams(params)}`,
        };
      },
    }),
    getHSNCodes: builder.query<BackendResponse<any>, any>({
      query: params => {
        return {
          url: `/master/hsn-code-dropdown${parseQueryParams(params)}`,
        };
      },
    }),
    getDistributionChannelById: builder.query<
      BackendResponse<any>,
      number | string
    >({
      query: id => ({
        url: `/master/distribution-channels/${id}`,
        method: 'GET',
      }),
    }),
    getCustomerGroup: builder.query<BackendResponse<any>, any>({
      query: params => {
        return {
          url: `/master/customer-groups/all${parseQueryParams(params)}`,
        };
      },
    }),
    getCustomerGroup1: builder.query<
      BackendResponse<any>,
      { sortOrder?: string } | void
    >({
      query: (params = { sortOrder: 'asc' }) => ({
        url: `/master/customer-groups1/all${parseQueryParams(params || { sortOrder: 'asc' })}`,
        method: 'GET',
      }),
    }),
    getPriceGroup: builder.query<BackendResponse<any>, any>({
      query: params => {
        return {
          url: `/master/price-group${parseQueryParams(params)}`,
        };
      },
    }),
    getPriceCategory: builder.query<BackendResponse<any>, any>({
      query: params => {
        return {
          url: `/master/price-category${parseQueryParams(params)}`,
        };
      },
    }),
    getState: builder.query<BackendResponse<any>, any>({
      query: params => {
        return {
          url: `/master/state${parseQueryParams(params)}`,
        };
      },
    }),
    getDivision: builder.query<BackendResponse<any>, any>({
      query: params => {
        return {
          url: `/master/divisions/all${parseQueryParams(params)}`,
        };
      },
    }),
    getCurrency: builder.query<BackendResponse<any>, any>({
      query: params => {
        return {
          url: `/master/currency${parseQueryParams(params)}`,
        };
      },
    }),
    getMaterialGroup: builder.query<BackendResponse<any>, any>({
      query: params => {
        return {
          url: `/master/material-groups/all${parseQueryParams(params)}`,
        };
      },
    }),
    getMaterial: builder.query<BackendResponse<any>, any>({
      query: params => {
        return {
          url: `/master/material/all${parseQueryParams(params)}`,
        };
      },
    }),
    getCustomer: builder.query<BackendResponse<any>, any>({
      query: params => {
        return {
          url: `/master/customers/all${parseQueryParams(params)}`,
        };
      },
    }),
    getCompanyBand: builder.query<BackendResponse<any>, any>({
      query: params => {
        return {
          url: `/master/bands/all${parseQueryParams(params)}`,
        };
      },
    }),
    getCompanyBandLevel: builder.query<BackendResponse<any>, any>({
      query: params => {
        return {
          url: `/master/bandlevels/all${parseQueryParams(params)}`,
        };
      },
    }),
    getEmploymentType: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/employment-types/all', method: 'GET' }),
    }),
    getAllDepartments: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/departments/all', method: 'GET' }),
    }),
    getAllGrades: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/employment-grades/all', method: 'GET' }),
    }),
    getAllCustomers: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/customers/all', method: 'GET' }),
    }),
    getContractPurposes: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/contract-purpose/all', method: 'GET' }),
    }),
    getAllMonths: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: 'master/claimMonths', method: 'GET' }),
    }),
    getClientSubCompanies: builder.query<
      BackendResponse<any>,
      Partial<{ companyGroupId: number }> | null
    >({
      query: query => ({
        url: `/master/companies/all${query ? parseQueryParams(query) : ''}`,
        method: 'GET',
      }),
    }),

    getCompanyGroup: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/company-groups/all', method: 'GET' }),
    }),

    getModules: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/system-module/all', method: 'GET' }),
    }),

    // Data Masking APIs
    getTables: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/prisma-models', method: 'GET' }),
    }),
    getTableColumns: builder.query<BackendResponse<any>, { tableName: string }>(
      {
        query: ({ tableName }) => ({
          url: `/master/table-columns?tableName=${tableName}`,
          method: 'GET',
        }),
      },
    ),
    getMaskingData: builder.query<
      IDataMaskingListResponse,
      IGetDataMaskingParams
    >({
      query: params => {
        return {
          url: `/master/data-masking${parseQueryParams(params)}`,
        };
      },
    }),
    getMaskingDataById: builder.query<IDataMaskingResponse, number>({
      query: id => ({
        url: `/master/data-masking/${id}`,
        method: 'GET',
      }),
    }),
    createMaskingData: builder.mutation<
      IDataMaskingResponse,
      ICreateDataMaskingDto
    >({
      query: body => ({
        url: '/master/data-masking',
        method: 'POST',
        body,
      }),
    }),
    updateMaskingData: builder.mutation<
      IDataMaskingResponse,
      { id: number; body: IUpdateDataMaskingDto }
    >({
      query: ({ id, body }) => ({
        url: `/master/data-masking/${id}`,
        method: 'PATCH',
        body,
      }),
    }),

    // Sales Order Master Data APIs
    getSalesOrderTypes: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/sales-order-types/all', method: 'GET' }),
    }),
    getPlants: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/plants/all', method: 'GET' }),
    }),
    searchPlants: builder.query<
      BackendResponse<any>,
      { search?: string; page?: number; pageSize?: number; limit?: number; warehouseOnly?: boolean }
    >({
      query: params => ({
        url: `/master/plants/search${parseQueryParams(params)}`,
        method: 'GET',
      }),
    }),
    getTaxCodes: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/tax-codes/all', method: 'GET' }),
    }),
    getBillTos: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/bill-tos/all', method: 'GET' }),
    }),
    getBillTosByCustomer: builder.query<BackendResponse<any>, number | string>({
      query: customerId => ({
        url: `/master/bill-tos/customer/${customerId}`,
        method: 'GET',
      }),
    }),
    getShipTos: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/ship-tos/all', method: 'GET' }),
    }),
    getShipTosByCustomer: builder.query<BackendResponse<any>, number | string>({
      query: customerId => ({
        url: `/master/ship-tos/customer/${customerId}`,
        method: 'GET',
      }),
    }),
    // Ship-To (new, paginated dropdown + create) - MasterController
    getShipToDropdown: builder.query<
      BackendResponse<any>,
      { page?: number; pageSize?: number; search?: string; customerId?: number | string }
    >({
      query: params => ({
        url: `/master/shipto${parseQueryParams(params as any)}`,
        method: 'GET',
      }),
    }),
    createShipToForCustomer: builder.mutation<
      BackendResponse<any>,
      {
        customerId: number | string;
        body: Record<string, any>;
      }
    >({
      query: ({ customerId, body }) => ({
        url: `/master/shipto/${customerId}`,
        method: 'POST',
        body,
      }),
    }),

    // Duplicate checks
    verifyCustomerName: builder.mutation<BackendResponse<any>, { name: string }>({
      query: body => ({
        url: '/master/customers/verify-name',
        method: 'POST',
        body,
      }),
    }),
    verifyMaterialLongDescription: builder.mutation<
      BackendResponse<any>,
      { name: string }
    >({
      query: body => ({
        url: '/master/material/verify-long-description',
        method: 'POST',
        body,
      }),
    }),
    getUOMs: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/uoms/all', method: 'GET' }),
    }),
    getIncoterms: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/incoterms/all', method: 'GET' }),
    }),
    getPaymentTerms: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/payment-terms/all', method: 'GET' }),
    }),
    getProductTypes: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/product-types/all', method: 'GET' }),
    }),
    getProductTypeById: builder.query<BackendResponse<any>, number | string>({
      query: id => ({
        url: `/master/product-types/${id}`,
        method: 'GET',
      }),
    }),
    getProductGroups: builder.query<
      BackendResponse<any>,
      { productTypeId?: number | null } | void
    >({
      query: params => ({
        url: `/master/product-groups/all${parseQueryParams(params as any)}`,
        method: 'GET',
      }),
    }),
    getGSTCodes: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/gst-codes/all', method: 'GET' }),
    }),
    getMaterialCategory: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/material-categories/all', method: 'GET' }),
    }),
    // Sales Order Material Groups (level-wise)
    getMaterialGroups1: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/material-groups-1/all', method: 'GET' }),
    }),
    getMaterialGroups2: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/material-groups-2/all', method: 'GET' }),
    }),
    getMaterialGroups3: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/material-groups-3/all', method: 'GET' }),
    }),
    getMaterialGroups4: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/material-groups-4/all', method: 'GET' }),
    }),

    // Create Material (basic)
    createMaterialBasic: builder.mutation<
      BackendResponse<any>,
      {
        ShortDescription?: string;
        LongDescription?: string;
        materialGroup1Id?: number | null;
        materialGroup2Id?: number | null;
        materialGroup3Id?: number | null;
        materialGroup4Id?: number | null;
        ProductTypeID?: number | null;
        ProductGroupID?: number | null;
        noOfCapsulePerUnit?: number | null;
        currencyId?: number | null;
        ratePerUnit?: number | null;
        uomId?: number | null;
        // Optional commercial/packaging fields (backend may ignore if unsupported)
        packSize?: string | number | null;
        packingType?: string | null;
        huType?: string | null;
        mrp?: string | number | null;
        shelfLifeInDays?: string | number | null;
        shelfLifeInMonths?: number | null;
      }
    >({
      query: body => ({
        url: '/master/material/basic',
        method: 'POST',
        body,
      }),
    }),

    // Get Material Sales Defaults
    getMaterialSalesDefaults: builder.query<
      BackendResponse<any>,
      | number
      | string
      | { materialId: number | string; customerId?: number | null }
    >({
      query: arg => {
        const materialId =
          typeof arg === 'object' && arg !== null && 'materialId' in arg
            ? arg.materialId
            : arg;
        const customerId =
          typeof arg === 'object' && arg !== null && 'customerId' in arg
            ? arg.customerId
            : undefined;
        const url = `/master/material/${materialId}/sales-defaults`;
        const params =
          customerId != null && Number.isFinite(Number(customerId))
            ? { customerId: String(customerId) }
            : undefined;
        return {
          url,
          method: 'GET',
          ...(params && { params }),
        };
      },
    }),

    // Packing Types Dropdown
    getPackingTypesDropdown: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/packing-types-dropdown', method: 'GET' }),
    }),
    // HU Type Dropdown
    getHuTypeDropdown: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/hutype', method: 'GET' }),
    }),

    // Region Dropdowns
    getRegions: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/contract-region', method: 'GET' }),
    }),
    getRegion1Dropdown: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/region1-dropdown', method: 'GET' }),
    }),
    getRegion2Dropdown: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/region2-dropdown', method: 'GET' }),
    }),
    getRegion3Dropdown: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/region3-dropdown', method: 'GET' }),
    }),

    // ── Purchase Order Master Dropdowns ──
    getPurchaseOrderTypes: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/purchase-order-types/all', method: 'GET' }),
    }),
    getPurchaseGroups: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/purchase-groups/all', method: 'GET' }),
    }),
    getProcurementCategories: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/procurement-categories/all', method: 'GET' }),
    }),
    getTdsTaxCodes: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/tds-tax-codes/all', method: 'GET' }),
    }),
    getAssetsDropdown: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/assets/all', method: 'GET' }),
    }),
    getProjectsDropdown: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/projects/all', method: 'GET' }),
    }),

    // ── Inline Creation Mutations ──
    createVendorInline: builder.mutation<BackendResponse<any>, { name: string; erpCode?: string }>({
      query: body => ({
        url: '/master/vendors',
        method: 'POST',
        body: [body],
      }),
    }),
    createCostCenterInline: builder.mutation<BackendResponse<any>, { name: string; erpCode?: string }>({
      query: body => ({
        url: '/master/cost-centers',
        method: 'POST',
        body: [body],
      }),
    }),
    createAssetInline: builder.mutation<BackendResponse<any>, { code: string; description?: string }>({
      query: body => ({
        url: '/master/assets',
        method: 'POST',
        body,
      }),
    }),
    createProjectInline: builder.mutation<BackendResponse<any>, { code: string; description?: string }>({
      query: body => ({
        url: '/activity-master/project',
        method: 'POST',
        body,
      }),
    }),
    createCompanyGroupModuleMapping: builder.mutation<
      BackendResponse<any>,
      { companyGroupId: number; moduleIds: number[] }
    >({
      query: payload => ({
        url: '/business-mapping/company-group-module',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['CompanyGroupModuleMapping'],
    }),
    getCompanyGroupModuleMappings: builder.query<
      BackendResponse<any>,
      any
    >({
      query: params => ({
        url: `/business-mapping/company-group-module${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['CompanyGroupModuleMapping'],
    }),
    getCompanyGroupModuleMappingById: builder.query<BackendResponse<any>, number>({
      query: id => ({
        url: `/business-mapping/company-group-module/${id}`,
        method: 'GET',
      }),
      providesTags: ['CompanyGroupModuleMapping'],
    }),
    updateCompanyGroupModuleMapping: builder.mutation<
      BackendResponse<any>,
      { id: number; moduleIds?: number[]; status?: number }
    >({
      query: ({ id, ...payload }) => ({
        url: `/business-mapping/company-group-module/${id}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: ['CompanyGroupModuleMapping'],
    }),
    deleteCompanyGroupModuleMapping: builder.mutation<
      BackendResponse<any>,
      number
    >({
      query: id => ({
        url: `/business-mapping/company-group-module/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CompanyGroupModuleMapping'],
    }),
    bulkDeleteCompanyGroupModuleMapping: builder.mutation<
      BackendResponse<any>,
      number[]
    >({
      query: ids => ({
        url: '/business-mapping/company-group-module/bulk-delete',
        method: 'POST',
        body: { ids },
      }),
      invalidatesTags: ['CompanyGroupModuleMapping'],
    }),
  }),
});

export const {
  useGetMasterChangeLogsQuery,
  useLazyGetMasterChangeLogsQuery,
  useGetSalesOrganisationQuery,
  useGetDistributionChannelQuery,
  useGetHSNCodesQuery,
  useGetDistributionChannelByIdQuery,
  useLazyGetDistributionChannelByIdQuery,
  useGetCustomerGroupQuery,
  useGetCustomerGroup1Query,
  useGetPriceGroupQuery,
  useGetPriceCategoryQuery,
  useGetStateQuery,
  useGetDivisionQuery,
  useGetCurrencyQuery,
  useGetMaterialGroupQuery,
  useGetMaterialQuery,
  useGetCustomerQuery,
  useGetCompanyBandQuery,
  useLazyGetCompanyBandLevelQuery,
  useGetEmploymentTypeQuery,
  useGetAllDepartmentsQuery,
  useGetAllGradesQuery,
  useGetAllCustomersQuery,
  useGetContractPurposesQuery,
  useGetAllMonthsQuery,
  useGetClientSubCompaniesQuery,
  useGetCompanyGroupQuery,
  useGetModulesQuery,
  // Data Masking hooks
  useGetTablesQuery,
  useGetTableColumnsQuery,
  useGetMaskingDataQuery,
  useLazyGetMaskingDataQuery,
  useGetMaskingDataByIdQuery,
  useLazyGetMaskingDataByIdQuery,
  useCreateMaskingDataMutation,
  useUpdateMaskingDataMutation,
  // Sales Order Master Data hooks
  useGetSalesOrderTypesQuery,
  useGetPlantsQuery,
  useLazySearchPlantsQuery,
  useGetTaxCodesQuery,
  useGetBillTosQuery,
  useGetBillTosByCustomerQuery,
  useGetShipTosQuery,
  useGetShipTosByCustomerQuery,
  useGetShipToDropdownQuery,
  useLazyGetShipToDropdownQuery,
  useCreateShipToForCustomerMutation,
  useVerifyCustomerNameMutation,
  useVerifyMaterialLongDescriptionMutation,
  useGetUOMsQuery,
  useGetIncotermsQuery,
  useGetPaymentTermsQuery,
  useGetProductTypesQuery,
  useGetProductTypeByIdQuery,
  useLazyGetProductTypeByIdQuery,
  useGetProductGroupsQuery,
  useGetGSTCodesQuery,
  useGetMaterialCategoryQuery,
  useGetMaterialGroups1Query,
  useGetMaterialGroups2Query,
  useGetMaterialGroups3Query,
  useGetMaterialGroups4Query,
  // Create Material basic
  useCreateMaterialBasicMutation,
  // Get Material Sales Defaults
  useLazyGetMaterialSalesDefaultsQuery,
  // Packing Types Dropdown
  useGetPackingTypesDropdownQuery,
  // HU Type Dropdown
  useGetHuTypeDropdownQuery,
  // Region Dropdowns
  useGetRegionsQuery,
  useGetRegion1DropdownQuery,
  useGetRegion2DropdownQuery,
  useGetRegion3DropdownQuery,
  // PO Master Dropdowns
  useGetPurchaseOrderTypesQuery,
  useGetPurchaseGroupsQuery,
  useGetProcurementCategoriesQuery,
  useGetTdsTaxCodesQuery,
  useGetAssetsDropdownQuery,
  useGetProjectsDropdownQuery,
  // Inline Creation Mutations
  useCreateVendorInlineMutation,
  useCreateCostCenterInlineMutation,
  useCreateAssetInlineMutation,
  useCreateProjectInlineMutation,
  useLazyGetHSNCodesQuery,
  // Company Group Module Mapping hooks
  useCreateCompanyGroupModuleMappingMutation,
  useGetCompanyGroupModuleMappingsQuery,
  useGetCompanyGroupModuleMappingByIdQuery,
  useUpdateCompanyGroupModuleMappingMutation,
  useDeleteCompanyGroupModuleMappingMutation,
  useBulkDeleteCompanyGroupModuleMappingMutation,
} = masterApi;

export default masterApi;
